import * as React from "react";
import { Hint } from "react-ui";

import { PicklistColumnMeta, PicklistDataItem } from "../../../../Engine/Core/Picklists/PicklistTypes";
import { getHintWidth } from "../../../constants";
import { isNullOrEmpty } from "../../../../../../Common/TypingUtils";
import { useAsyncEffect } from "../../../../../../Common/UseAsyncEffect";
import { getPickListItemsByValue } from "../Common/PicklistItemProvider";
import { Markdown } from "../../../Helpers/Markdown/Markdown";

import { Cell, Cells } from "./PicklistItemList/PicklistItem/PicklistItem.styles";
import { BasePicklistProps } from "./BasePicklistProps";

export interface BasePicklistHintProps extends BasePicklistProps {
    columnMetas?: PicklistColumnMeta[];
    hintOpened: boolean;
    hintWidth?: number;
    children: React.ReactNode;
    isEnableCaseIndependentValidation?: boolean;
}
export const BasePicklistHint = (props: BasePicklistHintProps): JSX.Element => {
    const [items, setItems] = React.useState<PicklistDataItem[]>([]);
    const [columnMetas, setColumnMetas] = React.useState<PicklistColumnMeta[]>([]);
    const hintFields = props.hintFields ?? [];

    useAsyncEffect(async () => {
        if (isNullOrEmpty(props.value)) {
            setItems([]);
            return;
        }
        const result = await getPickListItemsByValue(props.value, props, props.isEnableCaseIndependentValidation);
        setItems(result.items);
        setColumnMetas(result.columnMetas.filter(meta => hintFields.some(fieldName => meta.name === fieldName)));
    }, [props.value]);

    const maxLength = items.reduce((acc, item) => Math.max(acc, getItemLength(item, columnMetas)), 0);
    const hintMaxWidth = props.hintWidth ?? getHintWidth(maxLength);
    const hintText = items.map((item, index) => getItemHint(item, columnMetas, index));

    const hintOpened = props.hintOpened && maxLength > 0;

    return (
        <Hint maxWidth={hintMaxWidth} text={hintOpened ? hintText : undefined}>
            {props.children}
        </Hint>
    );
};

const getItemLength = (item: PicklistDataItem, columnMetas: PicklistColumnMeta[]): number =>
    columnMetas.reduce((acc, column) => acc + (item[column.name]?.toString().length ?? 0), 0);

const getItemHint = (item: PicklistDataItem, columnMetas: PicklistColumnMeta[], itemIndex: number): JSX.Element => {
    const hintKey = `hint_${itemIndex}`;
    const filledColumnsCount = columnMetas.filter(column => !!item[column.name]).length;

    const cells = columnMetas
        .filter(column => !!item[column.name])
        .map(column => {
            const name = column.name;
            const value = item[name]?.toString() ?? "";

            return (
                <Cell key={`${hintKey}_${name}`} textAlign={"left"} flexBasis={`${100 / filledColumnsCount}%`}>
                    {column.withMarkdown ? (
                        <Markdown
                            components={{
                                a: ({ children }) => <span>{children}</span>,
                            }}>
                            {value}
                        </Markdown>
                    ) : (
                        value
                    )}
                </Cell>
            );
        });

    return <Cells key={hintKey}>{cells}</Cells>;
};
